// ============================================================
// ✅ DepartementsListe.jsx - Liste des départements
// Support multi-tenant : Global + Spécifique
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { 
  Plus, Edit, Trash2, Search, 
  Building2, Globe, ChevronLeft, ChevronRight
} from 'lucide-react';
import DepartementModal from './DepartementModal';
import EditDepartementModal from './EditDepartementModal';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function DepartementsListe() {
  
  const [departements, setDepartements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartement, setSelectedDepartement] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departementToDelete, setDepartementToDelete] = useState(null);

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  useEffect(() => { fetchDepartements(); }, []);

  const fetchDepartements = async () => {
    setLoading(true);
    try {
      const res = await api.get('/departements', {
        params: { ID_Societe: isSuperAdmin ? null : user?.ID_Societe }
      });
      setDepartements(res.data.departements || []);
    } catch (err) {
      console.error('Erreur chargement départements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (dep) => { setSelectedDepartement(dep); setShowEditModal(true); };
  const handleDeleteClick = (dep) => { setDepartementToDelete(dep); setShowDeleteModal(true); };

  const filteredDepartements = departements.filter(d =>
    d.Nom_Departement?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDepartements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDepartements.length / itemsPerPage);

  const stats = {
    total: departements.length,
    globaux: departements.filter(d => d.EstGlobal === 1).length,
    specifiques: departements.filter(d => d.EstGlobal === 0).length
  };

  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen">
      
      <div className="mb-3">
        <h2 className="text-base md:text-lg font-bold text-gray-800">🏢 Départements</h2>
        <p className="text-[11px] text-gray-400">
          Gestion des départements {user?.SocieteNom ? `• ${user.SocieteNom}` : ''}
        </p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Total</p><p className="text-lg font-bold text-gray-800">{stats.total}</p></div>
            <Building2 size={18} className="text-emerald-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Globaux</p><p className="text-lg font-bold text-purple-600">{stats.globaux}</p></div>
            <Globe size={18} className="text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Spécifiques</p><p className="text-lg font-bold text-blue-600">{stats.specifiques}</p></div>
            <Building2 size={18} className="text-blue-500" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" placeholder="Rechercher..."
            className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg w-full sm:w-60 text-xs focus:ring-2 focus:ring-emerald-500"
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs flex items-center justify-center gap-1 transition shadow-sm">
          <Plus size={12} /> Nouveau département
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-500">
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-center">Services</th>
                <th className="px-3 py-2 text-center">Portée</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400 text-xs">Chargement...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400 text-xs">Aucun département</td></tr>
              ) : (
                currentItems.map(dep => (
                  <tr key={dep.ID_Departement} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{dep.ID_Departement}</td>
                    <td className="px-3 py-2">{dep.Nom_Departement}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700">{dep.NbServices || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {dep.EstGlobal === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                          <Globe size={10} /> Global
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                          <Building2 size={10} /> {dep.RaisonSociale || dep.Sigle || `ID: ${dep.ID_Societe}`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEditClick(dep)} className="p-1 text-blue-500 hover:text-blue-700 transition"><Edit size={14} /></button>
                        <button onClick={() => handleDeleteClick(dep)} className="p-1 text-red-500 hover:text-red-700 transition"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
            <p className="text-[10px] text-gray-400">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredDepartements.length)} sur {filteredDepartements.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={14} /></button>
              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      <DepartementModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={fetchDepartements} />
      <EditDepartementModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedDepartement(null); }} onSuccess={fetchDepartements} departement={selectedDepartement} />
      <DeleteConfirmModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setDepartementToDelete(null); }} onSuccess={fetchDepartements} departement={departementToDelete} />
    </div>
  );
}